import { InitiativeSupportStatus } from 'types/entities/initiativeSupport';
import { styled } from 'styled-components';

export const HeaderRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: ${({ theme }) => theme.sizes['3xl']};
  flex-wrap: wrap;

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    gap: ${({ theme }) => theme.sizes.sm};
    margin-bottom: ${({ theme }) => theme.sizes.lg};
  }
`;

export const PageTitle = styled.h1`
  color: ${({ theme }) => theme.colors.primary600};
  font-size: ${({ theme }) => theme.sizes['2xl']};
  font-weight: 700;
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;

  svg {
    width: 20px;
    height: 20px;
    flex-shrink: 0;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    font-size: ${({ theme }) => theme.sizes.lg};
  }
`;

export const SupportButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0;
  height: 56px;
  padding: 0 16px;
  border-radius: 8px;
  border: none;
  background-color: ${({ theme }) => theme.colors.secondary500};
  color: white;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
  flex-shrink: 0;

  span {
    font-size: 16px;
    font-weight: 600;
    line-height: 1;
    flex: 0 1 auto;
    white-space: nowrap;
    overflow: visible;
    text-overflow: clip;
  }

  svg {
    width: 20px;
    height: 20px;
    flex-shrink: 0;
    margin-left: 12px;
    display: block;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    width: 100%;
    justify-content: center;
    height: 48px;

    span {
      font-size: 14px;
    }
  }
`;

export const SupportStatusFeedback = styled.div<{
  $status: InitiativeSupportStatus | 'NOT_AVAILABLE';
}>`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.sizes.sm};
  margin: 0;
  padding: ${({ theme }) => theme.sizes.sm} ${({ theme }) => theme.sizes.md};
  border-radius: 8px;
  border: 1px solid
    ${({ theme, $status }) =>
      $status === 'CONFIRMED'
        ? theme.colors.green500
        : $status === 'NOT_AVAILABLE'
          ? theme.colors.neutral500
          : theme.colors.secondary500};
  background-color: ${({ theme, $status }) =>
    $status === 'CONFIRMED'
      ? theme.colors.green50
      : $status === 'NOT_AVAILABLE'
        ? theme.colors.neutral50
        : theme.colors.secondary50};
  color: ${({ theme, $status }) =>
    $status === 'CONFIRMED'
      ? theme.colors.green800
      : $status === 'NOT_AVAILABLE'
        ? theme.colors.neutral800
        : theme.colors.secondary800};
  font-size: ${({ theme }) => theme.sizes.sm};
  font-weight: 400;
  line-height: 1.35;
  flex-shrink: 0;

  svg {
    width: 24px;
    height: 24px;
    flex-shrink: 0;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    width: 100%;
    justify-content: center;
  }
`;

export const BackArrow = styled.div`
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 50%;
`;

export const MainVerticalGrid = styled.div`
  display: grid;
  grid-template-rows: auto auto;
  gap: 24px;
  align-content: start;
`;

export const TopSectionGrid = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto auto;
  grid-template-areas: 'projeto metricStack responsavel';
  gap: 24px;
  align-items: stretch;

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    grid-template-columns: 1fr;
    grid-template-areas:
      'projeto'
      'metricStack'
      'responsavel';
  }
`;

export const ColProjeto = styled.section`
  grid-area: projeto;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

export const ProjectLabel = styled.div`
  font-size: 18px;
  font-weight: 400;
  color: ${({ theme }) => theme.colors.neutral900};
`;

export const LearnerH2 = styled.h2`
  font-size: 40px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.neutral800};
  line-height: 1.1;
  margin: 0;
  word-break: break-word;

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    font-size: 32px;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    font-size: 24px;
  }
`;

export const LearnerH5 = styled.h5`
  font-size: 14px;
  font-style: italic;
  font-weight: 400;
  color: ${({ theme }) => theme.colors.neutral800};
  line-height: 1.1;
  margin: 0;
  margin-top: 4px;
  word-break: break-word;
`;

export const ChipRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

export const BlueChip = styled.div`
  padding: 8px 16px;
  border-radius: 18px;
  background-color: ${({ theme }) => theme.colors.blue200};
  color: ${({ theme }) => theme.colors.blue600};
  font-weight: 800;
  font-size: ${({ theme }) => theme.sizes['2xs']};
`;

export const ColMetricStack = styled.section`
  grid-area: metricStack;
  display: flex;
  flex-direction: column;
  gap: 16px;
  min-height: 0;
`;

export const MetricCard = styled.section`
  background: white;
  border-radius: 24px;
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  border: none;
  min-height: 0;
  min-width: 320px;

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    min-width: 0;
    padding: ${({ theme }) => theme.sizes.md};
    border-radius: 16px;
  }
`;

export const MetricTopRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 16px;
`;

export const IconSlot = styled.div`
  width: 38px;
  height: 38px;
  border-radius: 10px;
  background: transparent;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
`;

export const MetricValueRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: ${({ theme }) => theme.sizes.xs};
`;

export const MetricValue = styled.h3`
  margin: 0;
  font-size: ${({ theme }) => theme.sizes.md};
  font-weight: 700;
  color: ${({ theme }) => theme.colors.neutral800};
  line-height: 1.1;
  word-break: break-word;

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    font-size: ${({ theme }) => theme.sizes.lg};
  }
`;

export const MetricValueSuffix = styled.span`
  font-size: ${({ theme }) => theme.sizes.md};
  font-weight: 600;
  color: ${({ theme }) => theme.colors.primary600};
  line-height: 1.1;

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    font-size: ${({ theme }) => theme.sizes.lg};
  }
`;

export const MetricLabel = styled.div`
  font-size: 14px;
  font-weight: 400;
  color: ${({ theme }) => theme.colors.neutral500};
  margin-top: 4px;
`;

export const ProgressSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.sizes.xs};
  width: 100%;
  margin-top: auto;
`;

export const ProgressTrack = styled.div`
  width: 100%;
  height: 8px;
  border-radius: 999px;
  background: ${({ theme }) => theme.colors.neutral100};
  overflow: hidden;
`;

export const ProgressFooter = styled.span`
  font-size: ${({ theme }) => theme.sizes['2xs']};
  font-weight: 800;
  color: ${({ theme }) => theme.colors.neutral600};
  line-height: 1.35;
`;

export const ProgressFill = styled.div<{ $progress: number }>`
  width: ${({ $progress }) => $progress}%;
  height: 100%;
  border-radius: 999px;
  background: ${({ theme }) => theme.colors.primary600};
`;

export const ColResponsible = styled.section`
  grid-area: responsavel;
  min-height: 0;
  display: flex;
  flex-direction: column;
`;

export const ResponsibleCard = styled.section`
  background: white;
  border-radius: 24px;
  padding: 24px;
  display: flex;
  gap: 16px;
  align-items: stretch;
  flex: 1;
  min-height: 0;
  min-width: 320px;

  ${IconSlot} {
    align-self: flex-start;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    min-width: 0;
    padding: ${({ theme }) => theme.sizes.md};
    border-radius: 16px;
  }
`;

export const ResponsibleContent = styled.div`
  flex: 1;
  min-width: 0;
  min-height: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

export const ResponsibleContacts = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-top: 15px;
`;

export const ResponsibleName = styled.h3`
  margin: 0;
  font-size: 24px;
  font-weight: 900;
  color: ${({ theme }) => theme.colors.neutral800};
  line-height: 1.1;
  word-break: break-word;

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    font-size: 20px;
  }
`;

export const ResponsibleRole = styled.div`
  font-size: 14px;
  font-weight: 400;
  color: ${({ theme }) => theme.colors.neutral500};
`;

export const ContactLine = styled.p`
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.neutral800};
  line-height: 1.5;
`;

export const BottomSplit = styled.section`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 24px;
  align-items: stretch;

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    grid-template-columns: 1fr;
  }
`;

export const LeftMediaColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

export const MediaVisualizer = styled.div`
  width: 100%;
  min-height: 415px;
  max-height: 415px;
  flex: 1;
  border-radius: 24px;
  overflow: hidden;
  position: relative;
  background-color: ${({ theme }) => theme.colors.neutral100};

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    min-height: 280px;
    max-height: 280px;
    border-radius: 16px;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    min-height: 220px;
    max-height: 220px;
  }
`;

export const MediaVisualizerCover = styled.img`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: 85% center;
  display: block;
  max-width: 100%;
  max-height: 100%;
`;

export const CarouselNavButton = styled.button<{ $side: 'left' | 'right' }>`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  z-index: 2;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  padding: 0;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  background-color: ${({ theme }) => theme.colors.primary600};
  color: white;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);

  ${({ $side }) => ($side === 'left' ? 'left: 12px;' : 'right: 12px;')}

  &:hover {
    background-color: ${({ theme }) => theme.colors.primary500};
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.primary300};
    outline-offset: 2px;
  }

  svg {
    width: 20px;
    height: 20px;
    flex-shrink: 0;
  }
`;

/** Mesmo espaçamento título ↔ conteúdo que `TextBlock` (12px). */
export const InitiativeDocsSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

/** Alinhado ao DocList do passo Uploads (Uploads.style); margem 0 por causa do gap da coluna. */
export const InitiativeDocList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.sizes.sm};
  background-color: ${({ theme }) => theme.colors.neutral};
  padding: ${({ theme }) => theme.sizes.md};
  border-radius: 10px;
  border: 1px solid ${({ theme }) => theme.colors.neutral100};
`;

export const InitiativeDocRow = styled.li`
  display: grid;
  grid-template-columns: auto 1fr auto;
  align-items: center;
  gap: ${({ theme }) => theme.sizes.sm};
  padding: ${({ theme }) => theme.sizes.sm} ${({ theme }) => theme.sizes.md};
  border-radius: 10px;
  border: 1px solid ${({ theme }) => theme.colors.neutral100};
  background: ${({ theme }) => theme.colors.neutral50};
`;

export const InitiativeDocExtBadge = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 40px;
  padding: 6px 8px;
  border-radius: 6px;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.02em;
  color: ${({ theme }) => theme.colors.primary800};
  background: ${({ theme }) => theme.colors.primary100};
`;

export const InitiativeDocName = styled.span`
  min-width: 0;
  font-size: 14px;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.neutral800};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const InitiativeDocActions = styled.div`
  display: flex;
  flex-shrink: 0;
  gap: 2px;
  align-items: center;
`;

export const InitiativeDocDownloadLink = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 10px;
  border: none;
  border-radius: 8px;
  background: transparent;
  color: ${({ theme }) => theme.colors.primary700};
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
  text-decoration: none;

  &:hover {
    background: ${({ theme }) => theme.colors.primary50};
  }

  svg {
    width: 16px;
    height: 16px;
  }
`;

export const InitiativeDocsEmptyHint = styled.p`
  margin: 0;
  padding: ${({ theme }) => theme.sizes.md};
  border-radius: 8px;
  background: ${({ theme }) => theme.colors.neutral50};
  color: ${({ theme }) => theme.colors.neutral500};
  font-size: 14px;
  text-align: center;
`;

export const RightTextColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  min-height: 0;
`;

export const TextBlock = styled.div`
  min-height: 0;
  background: white;
  border-radius: 24px;
  border: 1px solid ${({ theme }) => theme.colors.neutral200};
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 12px;

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    padding: ${({ theme }) => theme.sizes.md};
    border-radius: 16px;
  }
`;

export const TextBlockTitle = styled.h6`
  margin: 0;
  font-size: 20px;
  font-weight: 900;
  color: ${({ theme }) => theme.colors.neutral900};
`;

export const InitiativeDocsListHeader = styled(TextBlockTitle)`
  text-align: center;
`;

export const TextParagraph = styled.p`
  margin: 0;
  font-size: 14px;
  font-weight: 400;
  color: ${({ theme }) => theme.colors.neutral800};
  line-height: 1.6;
  word-break: break-word;
  white-space: pre-line;
`;

export const TextList = styled.ul`
  margin: 0;
  padding-left: 1.25rem;
  font-size: 14px;
  font-weight: 400;
  color: ${({ theme }) => theme.colors.neutral800};
  line-height: 1.6;
  word-break: break-word;
`;

export const TextListItem = styled.li`
  &::marker {
    color: ${({ theme }) => theme.colors.neutral800};
  }
`;

export const InitiativeDateRow = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  gap: 4px;
`;

export const InitiativeDate = styled.span`
  font-size: 14px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.neutral800};
`;
